import { createContext, useContext, useEffect, useState, useCallback } from "react";

const InstallCtx = createContext({
  canPrompt: false,
  isIos: false,
  isStandalone: false,
  promptInstall: async () => false,
  showInstructions: () => {},
  instructionsOpen: false,
  closeInstructions: () => {},
});

function checkStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function checkIos() {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

export function InstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(checkStandalone());
  const [isIos] = useState(checkIos());
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setDeferred(null);
        return true;
      }
      return false;
    }
    // No native prompt available — show instructions dialog
    setInstructionsOpen(true);
    return false;
  }, [deferred]);

  const value = {
    canPrompt: !!deferred || isIos,
    isIos,
    isStandalone,
    promptInstall,
    showInstructions: () => setInstructionsOpen(true),
    instructionsOpen,
    closeInstructions: () => setInstructionsOpen(false),
  };

  return <InstallCtx.Provider value={value}>{children}</InstallCtx.Provider>;
}

export function useInstall() {
  return useContext(InstallCtx);
}
